import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { withMenu } from '../../../reactutils/withMenu';
import { withTitle } from '../../../reactutils/withTitle';
import { EventHandlerConfiguration } from '../../../services/account';
import { Section, SectionHeading } from '../../common/Section';
import { MenuSysAdmin } from '../../MenuSysAdmin';
import { getSystemConfig, ServerType } from '../../System/systemConfig';
import { eventHandlerConfigLoad } from './actions';
import { MissingEventHandlers } from './MissingEventHandlers';
import { eventHandlerConfigEntitySelector, eventHandlerConfigSelector } from './selectors';

interface Props {
    server: ServerType;
}

const createGraphViz = (e: EventHandlerConfiguration[]): string => {
    let graph = 'strict digraph EventHandler {';
    if (e) {
        e.forEach(n => {
            if (n.eventName) {
                graph += n.eventName + ' [shape=box] ;\n';
            }
            if (n.listenerName) {
                graph += n.listenerName + '[shape=component];\n';
            }
            if (n.destinations) {
                n.destinations.forEach(d => {
                    graph += d + ' [shape=ellipse] ;\n';
                    graph += n.eventName + ' -> ' + d + ';\n';
                });
            }
            if (n.receivingQueue) {
                graph += n.receivingQueue + ' -> ' + n.listenerName + ';\n';
            } else {
                graph += n.eventName + ' -> ' + n.listenerName + ' [color=red];\n';
            }
        });
    }
    graph += '}';
    return graph;
};

export const renderEventHandlerConfiguration = (e: EventHandlerConfiguration, index: number) => {
    return (
        <tr key={index}>
            <td>{e.eventName}</td>
            <td>
                {(e.destinations &&
                    e.destinations.map(d => (
                        <span key={d}>
                            {d}
                            <br />
                        </span>
                    ))) || <FontAwesomeIcon icon={faExclamationTriangle} color="red" />}
            </td>
            <td>{e.listenerName || <FontAwesomeIcon icon={faExclamationTriangle} color="red" />}</td>
            <td>{e.receivingQueue || <FontAwesomeIcon icon={faExclamationTriangle} color="red" />}</td>
        </tr>
    );
};

const EventHandlerConfig: FC<Props> = props => {
    const entities = useSelector(eventHandlerConfigEntitySelector(props.server));
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(eventHandlerConfigLoad(props.server));
    }, [dispatch, props.server]);

    return (
        <>
            <Section>
                <SectionHeading>{props.server}</SectionHeading>
                <small>
                    <Table>
                        <thead>
                            <tr>
                                <th>Event</th>
                                <th>Destinations</th>
                                <th>Listener</th>
                                <th>Receiving Queue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entities && entities.map((e, index) => renderEventHandlerConfiguration(e, index))}
                        </tbody>
                    </Table>
                </small>
            </Section>
        </>
    );
};

const EventHandlerConfigComp: FC<{}> = () => {
    const eventHandlerConfigState = useSelector(eventHandlerConfigSelector);

    const [showGraph, setShowGraph] = useState(false);
    const [graph, setGraph] = useState('digraph {}');

    const createOverallEventHandlerGraph = () => {
        const servers = Object.keys(getSystemConfig().serverUrls);

        const all = servers.flatMap(server =>
            eventHandlerConfigState[server] ? eventHandlerConfigState[server].handler : []
        );

        setGraph(createGraphViz(all));
        setShowGraph(true);
    };
    return (
        <>
            <Button color="secondary" outline={true} onClick={() => createOverallEventHandlerGraph()}>
                Generate DOT-File (GraphViz)
            </Button>

            <MissingEventHandlers />

            <EventHandlerConfig server="account" />
            <EventHandlerConfig server="betting" />
            <EventHandlerConfig server="image" />
            <EventHandlerConfig server="playlist" />

            <Modal toggle={() => setShowGraph(!showGraph)} isOpen={showGraph} size="lg">
                <ModalHeader>GraphViz Event Handler Config</ModalHeader>
                <ModalBody>
                    <Input type="textarea" value={graph} readOnly={true}></Input>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={() => setShowGraph(false)}>Schließen</Button>
                    <a
                        href={`https://dreampuf.github.io/GraphvizOnline/` + encodeURIComponent(graph)}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        View Graph
                    </a>
                </ModalFooter>
            </Modal>
        </>
    );
};

export const EventHandlerConfigScreen = R.compose(
    withMenu(<MenuSysAdmin />),
    withTitle('Event-Handler')
)(EventHandlerConfigComp);
